import {
  GAMEPAD_AXES,
  GAMEPAD_BINDINGS,
  INPUT_ACTIONS,
} from '../input/InputBindings.js';

export const LOCAL_COOP_INPUT_ROUTER_VERSION = 1;
export const LOCAL_COOP_KEYBOARD_DEVICE = 'keyboard-pointer';

const clamp = (value, min = -1, max = 1) => Math.min(max, Math.max(min, value));
const finiteOr = (value, fallback = 0) => Number.isFinite(value) ? value : fallback;
const clone = (value) => value === undefined ? undefined : JSON.parse(JSON.stringify(value));

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  for (const child of Object.values(value)) deepFreeze(child);
  return Object.freeze(value);
}

function canonicalize(value) {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.keys(value).sort().map((key) => [key, canonicalize(value[key])]),
  );
}

function checksumPayload(payload) {
  const serialized = JSON.stringify(canonicalize(payload));
  let hash = 0x811c9dc5;
  for (let index = 0; index < serialized.length; index += 1) {
    hash ^= serialized.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `fnv1a32:${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

export function computeLocalCoopInputRouterChecksum(snapshot) {
  if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) {
    throw new TypeError('snapshot must be an object');
  }
  const payload = clone(snapshot);
  delete payload.checksum;
  return checksumPayload(payload);
}

function assertPlainObject(value, name) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError(`${name} must be an object`);
  }
  return value;
}

function assertNonEmptyString(value, name) {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new TypeError(`${name} must be a non-empty string`);
  }
  return value.trim();
}

function assertNonNegativeInteger(value, name) {
  if (!Number.isInteger(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative integer`);
  }
  return value;
}

function applyDeadzone(value, deadzone) {
  const magnitude = Math.abs(value);
  if (magnitude <= deadzone) return 0;
  const normalized = (magnitude - deadzone) / (1 - deadzone);
  return Math.sign(value) * clamp(normalized, 0, 1);
}

function parseDeviceId(value) {
  const deviceId = assertNonEmptyString(value, 'deviceId');
  if (deviceId === LOCAL_COOP_KEYBOARD_DEVICE) {
    return { deviceId, kind: 'keyboard-pointer', gamepadIndex: null };
  }
  const match = /^gamepad:(\d+)$/.exec(deviceId);
  if (!match) {
    throw new RangeError('deviceId must be keyboard-pointer or gamepad:<index>');
  }
  return {
    deviceId,
    kind: 'gamepad',
    gamepadIndex: Number(match[1]),
  };
}

function neutralActionStates() {
  return Object.fromEntries(INPUT_ACTIONS.map((action) => [action, {
    down: false,
    pressed: false,
    released: false,
  }]));
}

function normalizeVector(value, fallback = { x: 0, y: 0 }) {
  if (!value || typeof value !== 'object') return { ...fallback };
  return {
    x: clamp(finiteOr(value.x, fallback.x)),
    y: clamp(finiteOr(value.y, fallback.y)),
  };
}

function normalizeLocalInput(input, playerId, tick) {
  assertPlainObject(input, 'localInput');
  if (input.version !== 1) throw new RangeError(`Unsupported local input version: ${input.version}`);
  return deepFreeze({
    ...clone(input),
    sources: {
      ...(clone(input.sources) ?? {}),
      localCoop: true,
      localCoopPlayerId: playerId,
      localCoopTick: tick,
      localCoopDevice: LOCAL_COOP_KEYBOARD_DEVICE,
    },
  });
}

function normalizeVirtualState(value = {}) {
  assertPlainObject(value, 'state');
  return {
    move: normalizeVector(value.move),
    look: normalizeVector(value.look),
    zoom: clamp(finiteOr(value.zoom)),
    actions: Object.fromEntries(INPUT_ACTIONS.map((action) => [action, Boolean(value.actions?.[action])])),
  };
}

function normalizePreviousActions(value, name) {
  assertPlainObject(value, name);
  return Object.fromEntries(INPUT_ACTIONS.map((action) => [action, Boolean(value[action])]));
}

export class LocalCoopInputRouter {
  constructor({
    navigatorRef = globalThis.navigator,
    gamepadDeadzone = 0.18,
    maxPlayers = 2,
  } = {}) {
    if (!Number.isFinite(gamepadDeadzone) || gamepadDeadzone < 0 || gamepadDeadzone >= 1) {
      throw new RangeError('gamepadDeadzone must be between 0 and 1');
    }
    if (!Number.isInteger(maxPlayers) || maxPlayers < 2) {
      throw new RangeError('maxPlayers must be an integer of at least 2');
    }
    this.navigatorRef = navigatorRef;
    this.gamepadDeadzone = gamepadDeadzone;
    this.maxPlayers = maxPlayers;
    this.assignments = new Map();
    this.virtualDevices = new Map();
    this.previousActions = new Map();
    this.sequences = new Map();
    this.revision = 0;
    this.lastTick = null;
    this.lastFrames = Object.freeze([]);
  }

  assignPlayer({ playerId, deviceId }) {
    const normalizedPlayerId = assertNonEmptyString(playerId, 'playerId');
    const device = parseDeviceId(deviceId);
    const existingPlayer = [...this.assignments.values()]
      .find((assignment) => assignment.deviceId === device.deviceId
        && assignment.playerId !== normalizedPlayerId);
    if (existingPlayer) {
      throw new RangeError(`Local co-op device is already assigned: ${device.deviceId}`);
    }
    if (!this.assignments.has(normalizedPlayerId) && this.assignments.size >= this.maxPlayers) {
      throw new RangeError(`Local co-op supports at most ${this.maxPlayers} players`);
    }
    const assignment = deepFreeze({ playerId: normalizedPlayerId, ...device });
    this.assignments.set(normalizedPlayerId, assignment);
    if (!this.previousActions.has(device.deviceId)) {
      this.previousActions.set(device.deviceId, Object.fromEntries(INPUT_ACTIONS.map((action) => [action, false])));
    }
    if (!this.sequences.has(device.deviceId)) this.sequences.set(device.deviceId, 0);
    this.revision += 1;
    return assignment;
  }

  unassignPlayer(playerId) {
    const normalizedPlayerId = assertNonEmptyString(playerId, 'playerId');
    const assignment = this.assignments.get(normalizedPlayerId) ?? null;
    if (!assignment) return null;
    this.assignments.delete(normalizedPlayerId);
    this.revision += 1;
    return assignment;
  }

  getAssignment(playerId) {
    const normalizedPlayerId = assertNonEmptyString(playerId, 'playerId');
    return this.assignments.get(normalizedPlayerId) ?? null;
  }

  listAssignments() {
    return deepFreeze([...this.assignments.values()]
      .map((assignment) => ({ ...assignment }))
      .sort((left, right) => left.playerId.localeCompare(right.playerId)));
  }

  setVirtualDeviceInput(deviceId, state = {}) {
    const device = parseDeviceId(deviceId);
    const normalized = deepFreeze(normalizeVirtualState(state));
    this.virtualDevices.set(device.deviceId, normalized);
    this.revision += 1;
    return normalized;
  }

  clearVirtualDeviceInput(deviceId) {
    const device = parseDeviceId(deviceId);
    const removed = this.virtualDevices.delete(device.deviceId);
    if (removed) this.revision += 1;
    return removed;
  }

  sampleAll({ tick, timestamp, localInput }) {
    const normalizedTick = assertNonNegativeInteger(tick, 'tick');
    if (!Number.isFinite(timestamp) || timestamp < 0) {
      throw new RangeError('timestamp must be a non-negative finite number');
    }
    if (this.lastTick !== null && normalizedTick <= this.lastTick) {
      throw new RangeError('local co-op input ticks must increase monotonically');
    }
    const frames = this.listAssignments().map((assignment) => {
      const input = assignment.kind === 'keyboard-pointer'
        ? normalizeLocalInput(localInput, assignment.playerId, normalizedTick)
        : this.#sampleGamepad(assignment, timestamp);
      return deepFreeze({
        playerId: assignment.playerId,
        tick: normalizedTick,
        deviceId: assignment.deviceId,
        input,
      });
    });
    this.lastTick = normalizedTick;
    this.lastFrames = deepFreeze(frames);
    return this.lastFrames;
  }

  snapshot() {
    const payload = {
      version: LOCAL_COOP_INPUT_ROUTER_VERSION,
      revision: this.revision,
      lastTick: this.lastTick,
      assignments: this.listAssignments().map((assignment) => ({ ...assignment })),
      previousActions: Object.fromEntries(
        [...this.previousActions.entries()]
          .sort(([left], [right]) => left.localeCompare(right))
          .map(([deviceId, actions]) => [deviceId, { ...actions }]),
      ),
      sequences: Object.fromEntries(
        [...this.sequences.entries()].sort(([left], [right]) => left.localeCompare(right)),
      ),
    };
    return deepFreeze({ ...payload, checksum: computeLocalCoopInputRouterChecksum(payload) });
  }

  restore(snapshot) {
    const parsed = typeof snapshot === 'string' ? JSON.parse(snapshot) : snapshot;
    assertPlainObject(parsed, 'snapshot');
    if (parsed.version !== LOCAL_COOP_INPUT_ROUTER_VERSION) {
      throw new RangeError(`Unsupported local co-op input snapshot version: ${parsed.version}`);
    }
    if (typeof parsed.checksum !== 'string'
      || parsed.checksum !== computeLocalCoopInputRouterChecksum(parsed)) {
      throw new RangeError('snapshot checksum is invalid');
    }
    if (!Array.isArray(parsed.assignments)) throw new TypeError('snapshot.assignments must be an array');
    assertPlainObject(parsed.previousActions, 'snapshot.previousActions');
    assertPlainObject(parsed.sequences, 'snapshot.sequences');
    const revision = assertNonNegativeInteger(parsed.revision, 'snapshot.revision');
    const lastTick = parsed.lastTick === null
      ? null
      : assertNonNegativeInteger(parsed.lastTick, 'snapshot.lastTick');

    const assignments = new Map();
    const devices = new Set();
    for (const value of parsed.assignments) {
      assertPlainObject(value, 'snapshot assignment');
      const playerId = assertNonEmptyString(value.playerId, 'snapshot assignment playerId');
      const device = parseDeviceId(value.deviceId);
      if (assignments.has(playerId)) throw new RangeError(`Duplicate local co-op player: ${playerId}`);
      if (devices.has(device.deviceId)) throw new RangeError(`Duplicate local co-op device: ${device.deviceId}`);
      assignments.set(playerId, deepFreeze({ playerId, ...device }));
      devices.add(device.deviceId);
    }
    if (assignments.size > this.maxPlayers) throw new RangeError('snapshot exceeds maxPlayers');

    const previousActions = new Map();
    for (const [deviceId, actions] of Object.entries(parsed.previousActions)) {
      parseDeviceId(deviceId);
      previousActions.set(deviceId, normalizePreviousActions(actions, `snapshot.previousActions.${deviceId}`));
    }
    const sequences = new Map();
    for (const [deviceId, sequence] of Object.entries(parsed.sequences)) {
      parseDeviceId(deviceId);
      sequences.set(deviceId, assertNonNegativeInteger(sequence, `snapshot.sequences.${deviceId}`));
    }

    this.assignments = assignments;
    this.previousActions = previousActions;
    this.sequences = sequences;
    this.revision = revision;
    this.lastTick = lastTick;
    this.lastFrames = Object.freeze([]);
    return this.snapshot();
  }

  reset() {
    this.assignments.clear();
    this.virtualDevices.clear();
    this.previousActions.clear();
    this.sequences.clear();
    this.revision = 0;
    this.lastTick = null;
    this.lastFrames = Object.freeze([]);
    return this.getStatus();
  }

  getStatus() {
    return deepFreeze({
      version: LOCAL_COOP_INPUT_ROUTER_VERSION,
      playerCount: this.assignments.size,
      revision: this.revision,
      lastTick: this.lastTick,
      assignments: this.listAssignments(),
      virtualDeviceIds: [...this.virtualDevices.keys()].sort(),
      checksum: this.snapshot().checksum,
    });
  }

  #sampleGamepad(assignment, timestamp) {
    const virtual = this.virtualDevices.get(assignment.deviceId) ?? null;
    let pad = null;
    if (!virtual) {
      try {
        pad = Array.from(this.navigatorRef?.getGamepads?.() ?? [])
          .find((candidate) => candidate
            && candidate.connected !== false
            && candidate.index === assignment.gamepadIndex) ?? null;
      } catch {
        pad = null;
      }
    }
    const axis = (index) => applyDeadzone(finiteOr(pad?.axes?.[index]), this.gamepadDeadzone);
    const buttonDown = (action) => {
      if (virtual) return Boolean(virtual.actions[action]);
      const buttonIndex = GAMEPAD_BINDINGS[action];
      const button = buttonIndex === undefined ? null : pad?.buttons?.[buttonIndex];
      return Boolean(button?.pressed || button?.value > 0.5);
    };
    const move = virtual?.move ?? {
      x: axis(GAMEPAD_AXES.moveX),
      y: -axis(GAMEPAD_AXES.moveY),
    };
    const look = virtual?.look ?? {
      x: axis(GAMEPAD_AXES.lookX),
      y: axis(GAMEPAD_AXES.lookY),
    };
    const zoom = virtual?.zoom ?? 0;
    const previous = this.previousActions.get(assignment.deviceId)
      ?? Object.fromEntries(INPUT_ACTIONS.map((action) => [action, false]));
    const current = {};
    const actions = {};
    for (const action of INPUT_ACTIONS) {
      const down = buttonDown(action);
      actions[action] = {
        down,
        pressed: down && !previous[action],
        released: !down && previous[action],
      };
      current[action] = down;
    }
    this.previousActions.set(assignment.deviceId, current);
    const sequence = (this.sequences.get(assignment.deviceId) ?? 0) + 1;
    this.sequences.set(assignment.deviceId, sequence);
    return deepFreeze({
      version: 1,
      sequence,
      timestamp,
      move: normalizeVector(move),
      look: normalizeVector(look),
      zoom: clamp(zoom),
      actions: actions ?? neutralActionStates(),
      sources: {
        keyboard: false,
        pointer: false,
        touch: false,
        gamepad: pad ? { index: pad.index, id: pad.id || 'Gamepad' } : null,
        virtual: Boolean(virtual),
        localCoop: true,
        localCoopPlayerId: assignment.playerId,
        localCoopDevice: assignment.deviceId,
        localCoopConnected: Boolean(virtual || pad),
      },
    });
  }
}
